import React, { useState, useEffect, useCallback } from "react";
import { Icon } from "@iconify/react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { useButtonTableStyle } from "../utils/useButtonTableStyle";
import Pagination from "../components/Pagination";
import {
  listRoles,
  isErpApiSuccess,
} from "../erpClient";
import { getRoleBadgeColor } from "./AddRole";
import RolePermissionsOverlay from "../components/permissions/RolePermissionsOverlay";

export function RoleBadge({ name, colorIndex }) {
  const c = getRoleBadgeColor(colorIndex);
  return (
    <span
      className="badge"
      style={{
        backgroundColor: c.bg,
        color: c.text,
        border: `1px solid ${c.text}33`,
        fontWeight: 600,
      }}
    >
      {name}
    </span>
  );
}

function RoleTable() {
  const navigate = useNavigate();
  const { buttonClass, tableClass, cardClass } = useButtonTableStyle();

  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const fetchRoles = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listRoles();
      if (isErpApiSuccess(res) && Array.isArray(res?.data?.roles)) {
        setRoles(res.data.roles);
      } else {
        setRoles([]);
        toast.error(res?.message || "Failed to load roles");
      }
    } catch (err) {
      console.error(err);
      setRoles([]);
      toast.error(
        err?.response?.data?.message || err?.message || "Failed to load roles",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  const filtered = roles.filter((r) =>
    String(r.name || "")
      .toLowerCase()
      .includes(searchQuery.toLowerCase()),
  );

  const totalCount = filtered.length;
  const paginated = filtered.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const handleEditClick = (role) => {
    navigate("/editrole", { state: { roleId: role.id, role } });
  };

  return (
    <div className="body-wrapper">
      <div className="container-fluid">
        <div
          className={`card w-100 position-relative overflow-hidden ${cardClass}`}
        >
          <div className="px-4 py-3 border-bottom d-flex justify-content-between align-items-center">
            <h4 className="card-title mb-0">Role Management</h4>
            <button
              id="btn-add-role"
              className={`btn btn-success ${buttonClass}`}
              onClick={() => navigate("/addrole")}
            >
              Add Role
            </button>
          </div>

          <div className="card-body p-4">
            <div className="d-flex justify-content-between mb-3">
              <input
                id="role-search"
                type="text"
                className={`form-control ${buttonClass}`}
                style={{ maxWidth: "260px" }}
                placeholder="Search roles..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>

            <div className={`table-responsive mb-4 border ${cardClass}`}>
              <table
                className={`table text-nowrap mb-0 align-middle ${tableClass}`}
              >
                <thead className="text-dark fs-4">
                  <tr>
                    <th>#</th>
                    <th>Role Name</th>
                    <th>Permissions</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="4" className="text-center">
                        Loading...
                      </td>
                    </tr>
                  ) : paginated.length > 0 ? (
                    paginated.map((role, idx) => {
                      const permCount = Array.isArray(role.permission_codes)
                        ? role.permission_codes.length
                        : 0;
                      return (
                        <tr key={role.id}>
                          <td>
                            {(currentPage - 1) * itemsPerPage + idx + 1}
                          </td>
                          <td>
                            <RoleBadge
                              name={role.name}
                              colorIndex={idx}
                            />
                          </td>
                          <td>
                            <div className="d-inline-flex align-items-center gap-2">
                              <span className="fw-medium text-dark">
                                {permCount}
                              </span>
                              <RolePermissionsOverlay role={role} />
                            </div>
                          </td>
                          <td>
                            <div className="d-flex gap-2">
                              <button
                                id={`btn-edit-role-${role.id}`}
                                className={`btn btn-primary btn-sm ${buttonClass}`}
                                onClick={() => handleEditClick(role)}
                                title="Edit Role"
                              >
                                <Icon
                                  icon="solar:pen-bold"
                                  width="16"
                                  height="16"
                                />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="4" className="text-center">
                        No roles found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <Pagination
              totalItems={totalCount}
              itemsPerPage={itemsPerPage}
              currentPage={currentPage}
              onPageChange={(p) => {
                const totalPages = Math.ceil(totalCount / itemsPerPage) || 1;
                if (p >= 1 && p <= totalPages) setCurrentPage(p);
              }}
              onItemsPerPageChange={(n) => {
                setItemsPerPage(n);
                setCurrentPage(1);
              }}
              itemsPerPageOptions={[10, 20, 30, 50]}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default RoleTable;
